/**
 * Ficha SEO pública /:slug (ej. /tokio).
 * La preview sigue en /preview/prebuilt/:guideId (no se toca).
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
	Box,
	Typography,
	Button,
	Chip,
	Grid,
	CircularProgress,
	Stack,
} from "@mui/material";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import TempleBuddhistOutlinedIcon from "@mui/icons-material/TempleBuddhistOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DirectionsTransitOutlinedIcon from "@mui/icons-material/DirectionsTransitOutlined";
import NightlifeOutlinedIcon from "@mui/icons-material/NightlifeOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import ElectricalServicesOutlinedIcon from "@mui/icons-material/ElectricalServicesOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ContactlessOutlinedIcon from "@mui/icons-material/ContactlessOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";
import LocalTaxiOutlinedIcon from "@mui/icons-material/LocalTaxiOutlined";
import FlightOutlinedIcon from "@mui/icons-material/FlightOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useFirebase } from "../context/ApiContext";
import { DashboardLayout } from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import { PurchaseChoiceModal } from "../components/PurchaseChoiceModal";
import ComingSoonModal from "../components/ComingSoonModal";
import EmailVerificationModal from "../components/EmailVerificationModal";
import { trackGuideInteraction, trackPurchaseAction } from "../utils/analytics";
import SeoHead from "../components/Seo/SeoHead";
import { getSiteOrigin } from "../config/site";
import { getLandingBySlug } from "../data/guideLandings";
import { landingJsonLd } from "../utils/seoSchema";
import { getCityPack } from "../data/routeBuilder";

import "../components/Dashboard/Dashboard.css";

import guiaKioto from "../assets/portada-kioto-web.png";
import kiotoLandingPromo from "../assets/kioto-landing-promo.png";
import guiaTokio from "../assets/portada-tokio-web.png";
import tokioLandingPromo from "../assets/tokio-landing-promo.png";
import guiaNY from "../assets/portada-ny-web.png";
import nyLandingPromo from "../assets/ny-landing-promo.png";
import guiaLondres from "../assets/guia-londres.png";
import londresLandingPromo from "../assets/londres-landing-promo.png";
import guiaSeulCover from "../assets/portada-seul-web.png";
import seulLandingPromo from "../assets/seul-landing-promo.png";
import guiaParis from "../assets/guia-paris.png";
import parisLandingPromo from "../assets/paris-landing-promo.png";
import guiaSanFrancisco from "../assets/guia-san-francisco.png";
import sanFranciscoLandingPromo from "../assets/san-francisco-landing-promo.png";
import guiaBangkok from "../assets/guia-bangkok.png";
import bangkokLandingPromo from "../assets/bangkok-landing-promo.png";
import guiaOsaka from "../assets/guia-osaka.png";
import osakaLandingPromo from "../assets/osaka-landing-promo.png";
import guiaKualaLumpur from "../assets/portada-kl-web.png";
import kualaLumpurLandingPromo from "../assets/kuala-lumpur-landing-promo.png";
import guiaBelgica from "../assets/portada-web-belgica.png";
import belgicaLandingPromo from "../assets/belgica-landing-promo.png";
import guiaPekin from "../assets/portada-web-pekin.png";
import pekinLandingPromo from "../assets/pekin-landing-promo.png";
import guiaBali from "../assets/portada-web-bali.png";
import baliLandingPromo from "../assets/bali-landing-promo.png";
import guiaEdimburgo from "../assets/guia-edimburgo.png";
import edimburgoLandingPromo from "../assets/edimburgo-landing-promo.png";
import plantillaTodo from "../assets/to-do.png";
import travelkitCover from "../assets/travel-kit2.png";

const imageMap = {
	kioto: guiaKioto,
	tokio: guiaTokio,
	ny: guiaNY,
	londres: guiaLondres,
	seul: seulLandingPromo || guiaSeulCover,
	paris: guiaParis,
	sanFrancisco: guiaSanFrancisco,
	bangkok: guiaBangkok,
	osaka: guiaOsaka,
	kualaLumpur: guiaKualaLumpur,
	belgica: guiaBelgica,
	pekin: guiaPekin,
	bali: guiaBali,
	edimburgo: guiaEdimburgo,
	"todo-viaje": plantillaTodo,
	travelkit: travelkitCover,
	japon: guiaTokio,
};

const promoMap = {
	ny: nyLandingPromo,
	seul: seulLandingPromo,
	osaka: osakaLandingPromo,
	kioto: kiotoLandingPromo,
	tokio: tokioLandingPromo,
	londres: londresLandingPromo,
	sanFrancisco: sanFranciscoLandingPromo,
	paris: parisLandingPromo,
	bangkok: bangkokLandingPromo,
	kualaLumpur: kualaLumpurLandingPromo,
	pekin: pekinLandingPromo,
	belgica: belgicaLandingPromo,
	bali: baliLandingPromo,
	edimburgo: edimburgoLandingPromo,
};

const ICONS = {
	temple: <TempleBuddhistOutlinedIcon sx={{ fontSize: 26 }} />,
	nightlife: <NightlifeOutlinedIcon sx={{ fontSize: 26 }} />,
	store: <StorefrontOutlinedIcon sx={{ fontSize: 26 }} />,
	transit: <DirectionsTransitOutlinedIcon sx={{ fontSize: 26 }} />,
	map: <MapOutlinedIcon sx={{ fontSize: 26 }} />,
	sparkle: <AutoAwesomeOutlinedIcon sx={{ fontSize: 26 }} />,
	museum: <AccountBalanceOutlinedIcon sx={{ fontSize: 26 }} />,
	food: <RestaurantOutlinedIcon sx={{ fontSize: 26 }} />,
	park: <ParkOutlinedIcon sx={{ fontSize: 26 }} />,
	beach: <BeachAccessOutlinedIcon sx={{ fontSize: 26 }} />,
	skyline: <LocationCityOutlinedIcon sx={{ fontSize: 26 }} />,
	route: <AltRouteOutlinedIcon sx={{ fontSize: 22 }} />,
	album: <CollectionsBookmarkOutlinedIcon sx={{ fontSize: 22 }} />,
	preview: <PlayCircleOutlineIcon sx={{ fontSize: 22 }} />,
	visa: <BadgeOutlinedIcon sx={{ fontSize: 22 }} />,
	ticket: <ConfirmationNumberOutlinedIcon sx={{ fontSize: 22 }} />,
	tip: <PaymentsOutlinedIcon sx={{ fontSize: 22 }} />,
	hotel: <HotelOutlinedIcon sx={{ fontSize: 22 }} />,
	plug: <ElectricalServicesOutlinedIcon sx={{ fontSize: 22 }} />,
	card: <CreditCardOutlinedIcon sx={{ fontSize: 22 }} />,
	omny: <ContactlessOutlinedIcon sx={{ fontSize: 22 }} />,
	direction: <SwapVertOutlinedIcon sx={{ fontSize: 22 }} />,
	taxi: <LocalTaxiOutlinedIcon sx={{ fontSize: 22 }} />,
	airport: <FlightOutlinedIcon sx={{ fontSize: 22 }} />,
};

const ACCENT = "#6366F1";

const formatPrice = (price, currency = "EUR") => {
	if (price == null) return "";
	try {
		return new Intl.NumberFormat("es-ES", {
			style: "currency",
			currency,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(price);
	} catch {
		return `${Number(price).toFixed(2)} €`;
	}
};

const GuideLandingPage = () => {
	const { slug } = useParams();
	const landing = getLandingBySlug(slug);
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		user,
		subscription,
		handleLogout: contextLogout,
		importPrebuiltGuide,
		getPrebuiltGuides,
		checkGuideAccess,
		purchaseGuide,
		purchaseSubscription,
		canCreateGuide,
		isBlockedByEmailVerification,
	} = useFirebase();

	const [featuredGuides, setFeaturedGuides] = useState([]);
	const [loadingGuides, setLoadingGuides] = useState(true);
	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("login");
	const [purchasingGuide, setPurchasingGuide] = useState(null);
	const [purchaseChoiceModal, setPurchaseChoiceModal] = useState({
		open: false,
		guide: null,
		limitInfo: null,
	});
	const [comingSoonModal, setComingSoonModal] = useState({
		open: false,
		guideName: null,
	});
	const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);

	const canonicalSlug = landing?.slug;
	const guideId = landing?.guideId;

	const guide = useMemo(() => {
		if (!landing) return null;
		const g = featuredGuides.find((x) => x.id === landing.guideId);
		const cover = imageMap[landing.guideId];
		if (g) return { ...g, coverImage: g.coverImage || cover };
		return {
			id: landing.guideId,
			name: `${landing.days ? `${landing.days} días en ` : ""}${landing.name}`,
			description: landing.lead,
			days: landing.days,
			tags: [landing.country],
			coverImage: cover,
			isFree: false,
			price: null,
			userHasAccess: false,
		};
	}, [featuredGuides, landing]);

	const seoPath = canonicalSlug ? `/${canonicalSlug}` : "/explorar";

	const jsonLd = useMemo(() => {
		if (!landing) return null;
		return landingJsonLd(landing, getSiteOrigin());
	}, [landing]);

	useEffect(() => {
		const load = async () => {
			setLoadingGuides(true);
			try {
				const prebuiltGuides = await getPrebuiltGuides();
				const mapped = (prebuiltGuides || []).map((item) => {
					const access = checkGuideAccess(item.id, item);
					return {
						...item,
						coverImage: imageMap[item.id] || item.coverImage,
						days: item.days || null,
						tags: item.tags || [],
						isFree: item.isFree,
						price: item.price,
						userHasAccess: item.userHasAccess ?? access.hasFullAccess,
						accessReason: item.accessReason ?? access.reason,
					};
				});
				setFeaturedGuides(mapped);
			} catch (e) {
				console.error(e);
				setFeaturedGuides([]);
			} finally {
				setLoadingGuides(false);
			}
		};
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, subscription, slug]);

	const formattedUser = user
		? {
				id: user.uid || user.id,
				displayName:
					user.displayName ||
					user.name ||
					user.email?.split("@")[0] ||
					"Usuario",
				email: user.email,
			}
		: null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home" || sectionId === "recent") {
			navigate("/");
			return;
		}
		if (sectionId === "explore") {
			navigate("/explorar");
			return;
		}
		if (sectionId === "my-guides") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus guías", {
					variant: "info",
				});
				return;
			}
			navigate("/mis-guias");
			return;
		}
		if (sectionId === "create") {
			navigate("/crear-guia");
			return;
		}
		if (sectionId === "favorites") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus favoritos", {
					variant: "info",
				});
				return;
			}
			navigate("/favoritos");
			return;
		}
		if (sectionId === "passport") {
			navigate("/passport");
			return;
		}
		if (sectionId === "travel-card") {
			navigate("/tarjeta-viajera");
			return;
		}
		if (sectionId === "modules") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus módulos", {
					variant: "info",
				});
				return;
			}
			navigate("/modulos");
			return;
		}
		if (sectionId === "discounts") {
			navigate("/descuentos");
			return;
		}
		if (sectionId === "destinos") {
			navigate("/destinos");
		}
	};

	const handleCreateFromScratch = () => {
		navigate("/crear-guia");
	};

	const handleLogout = async () => {
		await contextLogout();
		enqueueSnackbar("Sesión cerrada", { variant: "success" });
	};

	const handleViewGuide = () => {
		if (!guideId) return;
		trackGuideInteraction("preview_clicked", guideId, {
			source: `landing_${guideId}`,
		});
		navigate(`/preview/prebuilt/${guideId}`, {
			state: { from: `${location.pathname}${location.search}` },
		});
	};

	const handlePurchaseClick = () => {
		if (!guide?.id) return;
		if (!user) {
			setAuthMode("signup");
			setShowLogin(true);
			enqueueSnackbar("Regístrate para comprar la guía", { variant: "info" });
			return;
		}
		if (isBlockedByEmailVerification("payment")) {
			setShowEmailVerifyModal(true);
			return;
		}
		trackPurchaseAction("modal_opened", {
			guide_id: guideId,
			guide_name: guide?.name,
			source: `landing_${guideId}`,
		});
		setPurchaseChoiceModal({ open: true, guide });
	};

	const handleConfirmBuyGuide = async (g) => {
		if (!g?.id) return;
		setPurchasingGuide(g.id);
		try {
			await purchaseGuide(g.id);
		} catch (error) {
			enqueueSnackbar(error.message || "Error al procesar la compra", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		} finally {
			setPurchasingGuide(null);
		}
	};

	const handleConfirmSubscribe = async () => {
		setPurchasingGuide("sub");
		try {
			await purchaseSubscription();
		} catch (error) {
			enqueueSnackbar(error.message || "Error al iniciar la suscripción", {
				variant: "error",
			});
			setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null });
			throw error;
		} finally {
			setPurchasingGuide(null);
		}
	};

	const handleCustomizeGuide = async () => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			enqueueSnackbar("Inicia sesión para abrir la guía", { variant: "info" });
			return;
		}
		trackGuideInteraction("customize_clicked", guideId, {
			source: `landing_${guideId}`,
		});

		if (guide && !guide.isFree && !guide.userHasAccess) {
			enqueueSnackbar(
				`Necesitas comprar la guía o tener Premium (${guide.price ? formatPrice(guide.price) : ""})`,
				{ variant: "warning" },
			);
			return;
		}

		const hasAccessByEntitlement =
			guide?.userHasAccess || guide?.accessReason === "PACK";
		const createCheck = canCreateGuide();
		if (!createCheck.allowed && !hasAccessByEntitlement) {
			setPurchaseChoiceModal({
				open: true,
				guide,
				limitInfo: {
					currentGuides: createCheck.currentGuides,
					maxGuides: createCheck.maxGuides,
				},
			});
			return;
		}

		try {
			enqueueSnackbar("Abriendo guía...", { variant: "info" });
			const result = await importPrebuiltGuide(guideId);
			if (result?.project) {
				enqueueSnackbar(
					result.alreadyExists ? "Abriendo tu guía" : "Guía lista",
					{ variant: "success" },
				);
				const userId = result.project.userId || user.uid || user.id;
				navigate(`/usuarios/${userId}/guias/${result.project.id}`);
			} else {
				throw new Error("No se pudo abrir la guía");
			}
		} catch (error) {
			console.error(error);
			if (
				error.code === "GUIDE_LIMIT" ||
				error.message?.includes("límite de guías")
			) {
				setPurchaseChoiceModal({
					open: true,
					guide,
					limitInfo: {
						currentGuides: createCheck.currentGuides,
						maxGuides: createCheck.maxGuides,
					},
				});
			} else if (
				error.code === "ACCESS_DENIED" ||
				error.message?.includes("acceso")
			) {
				enqueueSnackbar("No tienes acceso a esta guía todavía.", {
					variant: "warning",
				});
			} else if (
				error.response?.data?.error === "EMAIL_NOT_VERIFIED" ||
				error.message?.includes("verificar tu email")
			) {
				setShowEmailVerifyModal(true);
			} else {
				enqueueSnackbar(error.message || "No se pudo abrir la guía", {
					variant: "error",
				});
			}
		}
	};

	if (!landing) {
		return <Navigate to="/destinos" replace />;
	}

	if (slug !== canonicalSlug) {
		return <Navigate to={`/${canonicalSlug}`} replace />;
	}

	const showPrimaryBuy = !guide.userHasAccess && !guide.isFree;
	const priceLabel =
		guide.price != null
			? formatPrice(guide.price, guide.currency || "EUR")
			: null;
	const showLoadingShell = loadingGuides && featuredGuides.length === 0;
	const heroImage = promoMap[landing.guideId] || guide.coverImage;
	const quickFacts =
		landing.quickFacts ||
		[
			landing.days ? `${landing.days} días` : null,
			"Itinerario",
			"Mapas",
			"Lista para editar",
		].filter(Boolean);
	const includeSpots = (landing.spots || []).slice(0, 4);
	const hasRoutePack = Boolean(
		getCityPack(landing.slug) || getCityPack(landing.guideId),
	);
	const hubItems = (landing.hub || []).filter((item) => {
		if (item.to === "/crear-ruta" || item.icon === "route") {
			return hasRoutePack;
		}
		return true;
	});
	const hubCols = hubItems.length >= 3 ? 4 : hubItems.length === 2 ? 6 : 12;

	const primaryAction = {
		label: "Ver la guía",
		onClick: handleViewGuide,
		buy: false,
	};
	const secondaryAction = showPrimaryBuy
		? {
				label: priceLabel ? `Comprar — ${priceLabel}` : "Comprar guía",
				onClick: handlePurchaseClick,
				buy: true,
			}
		: { label: "Empezar mi guía", onClick: handleCustomizeGuide, buy: false };

	return (
		<>
			<SeoHead
				title={landing.seoTitle}
				description={landing.seoDescription}
				path={seoPath}
				imagePath={landing.ogImage}
				jsonLd={jsonLd}
			/>
			<DashboardLayout
				user={formattedUser}
				activeSection="destinos"
				onSectionChange={handleSectionChange}
				onLogin={() => {
					setAuthMode("login");
					setShowLogin(true);
				}}
				onSignUp={() => {
					setAuthMode("signup");
					setShowLogin(true);
				}}
				onLogout={handleLogout}
				onCreateNew={handleCreateFromScratch}
				onSearch={() => {}}
				prebuiltGuides={featuredGuides}
				myGuides={[]}
				imageMap={imageMap}
				onComingSoonGuide={(g) =>
					setComingSoonModal({ open: true, guideName: g?.name ?? null })
				}
				quietCreate
			>
				<Box sx={{ mt: { xs: 2, md: 0 } }}>
					{showLoadingShell ? (
						<Box
							sx={{
								minHeight: "48vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<CircularProgress sx={{ color: ACCENT }} />
						</Box>
					) : (
						<Box sx={{ maxWidth: 1080, mx: "auto", pb: 6 }}>
							{/* Hero header — panel tintado como bloque destacado */}
							<Box
								sx={{
									mb: { xs: 4, md: 5 },
									p: { xs: 2.5, sm: 3, md: 4 },
									borderRadius: { xs: "20px", md: "24px" },
									background:
										"linear-gradient(145deg, #EEF2FF 0%, #F5F3FF 48%, #F8FAFC 100%)",
									border: "1px solid rgba(99, 102, 241, 0.12)",
									boxShadow: "0 10px 30px -18px rgba(79, 70, 229, 0.22)",
								}}
							>
								<Box
									component="button"
									type="button"
									onClick={() => navigate("/destinos")}
									sx={{
										display: "inline-flex",
										alignItems: "center",
										gap: 0.75,
										mb: 2.5,
										p: 0,
										border: "none",
										background: "none",
										cursor: "pointer",
										fontFamily: "inherit",
										color: "#64748b",
										fontWeight: 600,
										fontSize: "0.875rem",
										position: "relative",
										zIndex: 2,
										"&:hover": { color: "#4f46e5" },
									}}
								>
									<ArrowBackIcon sx={{ fontSize: 18 }} />
									Destinos
								</Box>
								<Grid
									container
									spacing={{ xs: 3, md: 5 }}
									alignItems="center"
								>
								<Grid item xs={12} md={7}>
									<Chip
										label={landing.chip}
										size="small"
										sx={{
											mb: 2,
											fontWeight: 600,
											backgroundColor: "rgba(99, 102, 241, 0.1)",
											color: "#4338ca",
											border: "none",
										}}
									/>
									<Typography
										variant="h4"
										component="h1"
										sx={{
											fontWeight: 800,
											letterSpacing: "-0.03em",
											color: "#0f172a",
											fontSize: {
												xs: "1.7rem",
												sm: "2.05rem",
												md: "2.4rem",
											},
											lineHeight: 1.15,
											mb: 2,
										}}
									>
										{landing.h1}
									</Typography>
									<Typography
										sx={{
											color: "#475569",
											fontSize: { xs: "1rem", md: "1.0625rem" },
											lineHeight: 1.65,
											mb: 2.5,
											maxWidth: 540,
										}}
									>
										{landing.lead}
									</Typography>
									<Stack
										direction="row"
										flexWrap="wrap"
										useFlexGap
										spacing={1}
										sx={{ mb: 3 }}
									>
											{quickFacts.map((fact) => (
												<Chip
													key={fact}
													label={fact}
													sx={{
														fontWeight: 600,
														fontSize: "0.8125rem",
														backgroundColor: "rgba(255, 255, 255, 0.85)",
														color: "#334155",
														border: "1px solid rgba(99, 102, 241, 0.12)",
													}}
												/>
											))}
									</Stack>
									<Stack
										direction={{ xs: "column", sm: "row" }}
										spacing={1.5}
									>
										<Button
											variant="contained"
											size="large"
											onClick={primaryAction.onClick}
											startIcon={<PlayCircleOutlineIcon />}
											sx={{
												textTransform: "none",
												fontWeight: 700,
												borderRadius: 2,
												px: 2.5,
												py: 1.25,
												boxShadow: "0 8px 24px -8px rgba(99, 102, 241, 0.55)",
											}}
										>
											{primaryAction.label}
										</Button>
										<Button
											variant="outlined"
											size="large"
											onClick={secondaryAction.onClick}
											startIcon={
												secondaryAction.buy ? (
													<ShoppingBagOutlinedIcon />
												) : null
											}
											sx={{
												textTransform: "none",
												fontWeight: 600,
												borderRadius: 2,
												"&&": {
													color: "#4f46e5 !important",
													border:
														"1px solid rgba(99, 102, 241, 0.45) !important",
												},
												"&:hover": {
													borderColor: "#6366f1 !important",
													backgroundColor:
														"rgba(99, 102, 241, 0.06) !important",
												},
											}}
										>
											{secondaryAction.label}
										</Button>
									</Stack>
								</Grid>
								<Grid item xs={12} md={5}>
									<Box
										sx={{
											borderRadius: 3,
											overflow: "hidden",
											lineHeight: 0,
											boxShadow: "0 18px 40px -22px rgba(15, 23, 42, 0.35)",
											transform: { md: "rotate(1.2deg)" },
										}}
									>
										<Box
											component="img"
											src={heroImage}
											alt={`${landing.seoTitle} — Viajes Molones`}
											sx={{
												width: "100%",
												height: "auto",
												display: "block",
											}}
										/>
									</Box>
								</Grid>
								</Grid>
							</Box>

							{landing.cityFacts?.length > 0 && (
								<LandingBlock
									title={landing.cityFactsTitle || "Antes de ir"}
									lead={landing.cityFactsLead}
								>
									<Grid container spacing={2}>
										{landing.cityFacts.map((item) => (
											<Grid item xs={12} sm={6} key={item.title}>
												<InfoCard
													icon={item.icon}
													title={item.title}
													text={item.text}
												/>
											</Grid>
										))}
									</Grid>
								</LandingBlock>
							)}

							{landing.neighborhoods?.length > 0 && (
								<LandingBlock
									title={
										landing.neighborhoodsTitle || "Barrios que importan"
									}
									lead={landing.neighborhoodsLead}
								>
									<Grid container spacing={2}>
										{landing.neighborhoods.map((row, index) => (
											<Grid item xs={12} sm={6} key={row.name}>
												<InfoCard
													badge={String(index + 1).padStart(2, "0")}
													title={row.name}
													text={row.text}
												/>
											</Grid>
										))}
									</Grid>
								</LandingBlock>
							)}

							{!landing.cityFacts?.length && (
								<LandingBlock title={landing.whyTitle} lead={landing.whyLead}>
									<Grid container spacing={2}>
										{includeSpots.map((item) => (
											<Grid item xs={12} sm={6} key={item.title}>
												<InfoCard
													icon={item.icon}
													title={item.title}
													text={item.text}
												/>
											</Grid>
										))}
									</Grid>
								</LandingBlock>
							)}

							{landing.itineraryPreview?.length > 0 && (
								<LandingBlock
									title={
										landing.itineraryTitle ||
										`Itinerario de ${landing.days} días`
									}
									lead={landing.itineraryLead}
								>
									<Stack
										spacing={1.25}
										sx={{
											p: { xs: 1.5, sm: 2 },
											borderRadius: "20px",
											border: "1px solid #ececf4",
											backgroundColor: "#fff",
										}}
									>
										{landing.itineraryPreview.map((row) => (
											<Box
												key={row.day}
												sx={{
													display: "flex",
													alignItems: "flex-start",
													gap: 1.75,
													px: { xs: 1.25, sm: 1.5 },
													py: 1.5,
													borderRadius: "14px",
													backgroundColor: "#f8fafc",
												}}
											>
												<Box
													sx={{
														minWidth: 44,
														height: 44,
														borderRadius: "12px",
														background:
															"linear-gradient(135deg, rgba(99,102,241,0.16) 0%, rgba(139,92,246,0.1) 100%)",
														display: "flex",
														flexDirection: "column",
														alignItems: "center",
														justifyContent: "center",
														flexShrink: 0,
													}}
												>
													<Typography
														sx={{
															fontSize: "0.625rem",
															fontWeight: 700,
															color: "#6366f1",
															lineHeight: 1,
															letterSpacing: "0.04em",
														}}
													>
														DÍA
													</Typography>
													<Typography
														sx={{
															fontWeight: 800,
															color: "#4f46e5",
															fontSize: "1rem",
															lineHeight: 1.15,
														}}
													>
														{row.day}
													</Typography>
												</Box>
												<Box sx={{ pt: 0.25, display: "flex", alignItems: "center", minHeight: 44 }}>
													<Typography
														sx={{
															color: "#0f172a",
															fontWeight: 700,
															fontSize: "0.98rem",
														}}
													>
														{row.title}
													</Typography>
												</Box>
											</Box>
										))}
									</Stack>
								</LandingBlock>
							)}

							{landing.gettingAround && (
								<LandingBlock
									title={landing.gettingAround.title}
									lead={landing.gettingAround.lead}
								>
									<Grid container spacing={2}>
										{landing.gettingAround.items.map((item) => (
											<Grid item xs={12} sm={6} key={item.title}>
												<InfoCard
													icon={item.icon}
													title={item.title}
													text={item.text}
												/>
											</Grid>
										))}
									</Grid>
								</LandingBlock>
							)}

							{hubItems.length > 0 && (
								<LandingBlock title="Prepara tu viaje">
									<Grid container spacing={2}>
										{hubItems.map((item) => (
											<Grid item xs={12} md={hubCols} key={item.title}>
												<InfoCard
													icon={item.icon}
													title={item.title}
													text={item.text}
													to={item.to}
												/>
											</Grid>
										))}
									</Grid>
								</LandingBlock>
							)}
						</Box>
					)}
				</Box>
			</DashboardLayout>

			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>

			<PurchaseChoiceModal
				open={purchaseChoiceModal.open}
				guide={purchaseChoiceModal.guide}
				onClose={() =>
					setPurchaseChoiceModal({ open: false, guide: null, limitInfo: null })
				}
				onBuyGuide={handleConfirmBuyGuide}
				onSubscribe={handleConfirmSubscribe}
				loading={!!purchasingGuide}
				limitInfo={purchaseChoiceModal.limitInfo}
			/>

			<ComingSoonModal
				open={comingSoonModal.open}
				onClose={() => setComingSoonModal({ open: false, guideName: null })}
				guideName={comingSoonModal.guideName}
			/>

			<EmailVerificationModal
				open={showEmailVerifyModal}
				onClose={() => setShowEmailVerifyModal(false)}
			/>
		</>
	);
};

const CARD_SX = {
	height: "100%",
	p: 2.25,
	borderRadius: "16px",
	border: "1px solid #ececf4",
	backgroundColor: "#fff",
};

const ICON_WRAP_SX = {
	width: 44,
	height: 44,
	borderRadius: "12px",
	background:
		"linear-gradient(135deg, rgba(99,102,241,0.16) 0%, rgba(139,92,246,0.1) 100%)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	color: "#4f46e5",
	flexShrink: 0,
	mb: 1.5,
};

const LandingBlock = ({ title, lead, children }) => (
	<Box sx={{ mb: { xs: 5, md: 7 } }}>
		<Typography
			variant="h5"
			sx={{
				fontWeight: 800,
				letterSpacing: "-0.02em",
				color: "#0f172a",
				mb: lead ? 0.75 : 2,
				fontSize: { xs: "1.25rem", md: "1.45rem" },
			}}
		>
			{title}
		</Typography>
		{lead && (
			<Typography
				sx={{
					color: "#64748b",
					mb: 2.5,
					maxWidth: 680,
					fontSize: "0.98rem",
					lineHeight: 1.6,
				}}
			>
				{lead}
			</Typography>
		)}
		{children}
	</Box>
);

const InfoCard = ({ icon, badge, title, text, to }) => {
	const location = useLocation();
	const interactive = Boolean(to);
	const linkState =
		typeof to === "string" && to.startsWith("/")
			? { from: `${location.pathname}${location.search}` }
			: undefined;
	return (
		<Box
			component={interactive ? Link : "div"}
			to={to}
			state={linkState}
			sx={{
				...CARD_SX,
				display: "block",
				textDecoration: "none",
				transition: "box-shadow .2s ease, transform .2s ease, border-color .2s ease",
				...(interactive
					? {
							"&:hover": {
								boxShadow: "0 16px 36px -20px rgba(15, 23, 42, 0.28)",
								transform: { sm: "translateY(-2px)" },
								borderColor: "#c7d2fe",
							},
						}
					: null),
			}}
		>
			{(icon || badge) && (
				<Box sx={ICON_WRAP_SX}>
					{badge ? (
						<Typography
							sx={{
								fontWeight: 800,
								fontSize: "0.875rem",
								color: "#4f46e5",
								letterSpacing: "-0.02em",
							}}
						>
							{badge}
						</Typography>
					) : (
						ICONS[icon] || ICONS.map
					)}
				</Box>
			)}
			<Typography
				sx={{
					fontWeight: 700,
					color: "#0f172a",
					mb: 0.6,
					fontSize: "0.98rem",
					letterSpacing: "-0.01em",
				}}
			>
				{title}
			</Typography>
			<Typography
				sx={{
					color: "#64748b",
					fontSize: "0.9rem",
					lineHeight: 1.6,
				}}
			>
				{text}
			</Typography>
		</Box>
	);
};

export default GuideLandingPage;
